import { List, ListItem } from 'foundations-components/transitional/components/List';
import { isCustomCapability } from '../../editorCapabilities';
import InsertMenuTypeButton, { InsertMenuUpgradeButton } from './InsertMenuTypeButton';
import Raven from 'raven-js';
import InsertMeetings from '../../commands/meetings/InsertMeetings';
import InsertDocuments from '../../commands/documents/InsertDocuments';
import { PaymentsEligibilityStatus } from '../../commands/paymentLink/PaymentLinksApi';
import { INSERT_HUBSPOT_VIDEO_GATE } from '../../commands/video/videoHooks';
import { useCallback, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { PaymentLinksEligibilityContext } from '../../contexts/PaymentLinksEligibilityContext';
import InlineInsertHeader from './InlineInsertHeader';
import GenericInserter from '../../inserters/GenericInserter';
import { INSERTER_DEFINITIONS } from '../../inserters/definitions';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getHasAccess = (type, auth, paymentsEligibilityStatus) => {
  if (!auth) {
    return true;
  }
  if (isCustomCapability(type)) {
    return true;
  }
  const {
    scopes
  } = auth.user;
  switch (type) {
    case 'MEETINGS':
      return true;
    case 'TEMPLATES':
      return true;
    case 'KNOWLEDGE_ARTICLE':
      return scopes.includes('service-knowledge-access') ? true : 'inbox-insert-knowledge-article';
    case 'PAYMENT_LINK':
      return scopes.includes('payment-links-read') && paymentsEligibilityStatus === PaymentsEligibilityStatus.ACCOUNT_EXISTS;
    case 'INVOICES':
      return scopes.includes('invoices-read');
    case 'VIDEO_INSERTION':
      return scopes.includes('one-to-one-video') || auth.gates.includes(INSERT_HUBSPOT_VIDEO_GATE);
    case 'SNIPPETS':
      return scopes.includes('snippets-read-access') || scopes.includes('snippets-limited-read-access');
    case 'DOCUMENTS':
      return true;
    case 'QUOTES':
      return scopes.includes('quote-access');
    case 'SIGNATURE':
      return true;
    default:
      Raven.captureMessage('NO_HAS_ACCESS');
      return false;
  }
};
const InsertMenuItems = ({
  auth,
  editorConfig,
  handleSelectType,
  insertTypeOrder,
  paymentsEligibilityStatus,
  handleOpenModal
}) => {
  return /*#__PURE__*/_jsx(List, {
    wrapChildren: true,
    "data-test-id": "insert-menu-list",
    children: insertTypeOrder.map(type => {
      const access = getHasAccess(type, auth, paymentsEligibilityStatus);
      if (!access || !editorConfig[type]) {
        return null;
      } else if (typeof access === 'string') {
        return /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(InsertMenuUpgradeButton, {
            type: type,
            editorConfig: editorConfig,
            upgradeKey: access
          }, type)
        }, type);
      } else {
        return /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(InsertMenuTypeButton, {
            editorConfig: editorConfig,
            type: type,
            onSelectMediaType: handleSelectType,
            onOpenModal: handleOpenModal
          }, type)
        }, type);
      }
    })
  });
};
export default function InsertMenu({
  editorConfig,
  selectedType,
  handleSelectType,
  insertTypeOrder,
  handleViewInsertMenu,
  handleInsertComplete,
  handleOpenModal
}) {
  const auth = useContext(AuthContext);
  const paymentsEligibilityStatus = useContext(PaymentLinksEligibilityContext);
  const resetSelectedType = useCallback(() => handleSelectType(''), [handleSelectType]);
  if (!selectedType) {
    return /*#__PURE__*/_jsx(InsertMenuItems, {
      auth: auth,
      editorConfig: editorConfig,
      handleSelectType: handleSelectType,
      handleOpenModal: handleOpenModal,
      insertTypeOrder: insertTypeOrder,
      paymentsEligibilityStatus: paymentsEligibilityStatus
    });
  }
  if (selectedType === 'VIDEO_INSERTION' || selectedType === 'SIGNATURE') {
    return null;
  }
  if (isCustomCapability(selectedType)) {
    const insertMenuEntry = editorConfig[selectedType];
    if (!insertMenuEntry || !insertMenuEntry.menuItem) return null;
    return insertMenuEntry.menuItem({
      onInsertComplete: handleInsertComplete,
      onViewInsertMenu: resetSelectedType
    });
  }

  // Definition-driven rendering for standard inserters
  const definition = INSERTER_DEFINITIONS[selectedType];
  if (definition) {
    return /*#__PURE__*/_jsx(View, {
      paddingBlockStart: "300",
      paddingBlockEnd: "200",
      as: "div",
      children: /*#__PURE__*/_jsx(GenericInserter, {
        type: selectedType,
        definition: definition,
        editorConfig: editorConfig,
        onViewInsertMenu: handleViewInsertMenu,
        onInsertComplete: handleInsertComplete
      })
    });
  }

  // Special cases with InlineInsertHeader or custom rendering
  switch (selectedType) {
    case 'MEETINGS':
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(InlineInsertHeader, {
          onBackClick: resetSelectedType,
          selectedType: selectedType
        }), /*#__PURE__*/_jsx(View, {
          paddingBlockStart: "300",
          paddingBlockEnd: "200",
          as: "div",
          children: /*#__PURE__*/_jsx(InsertMeetings, {
            outputType: editorConfig[selectedType].outputType,
            onViewInsertMenu: handleViewInsertMenu,
            onInsertComplete: handleInsertComplete,
            onInsertContent: editorConfig[selectedType].onInsertContent,
            useContentContainer: true,
            resetSelectedType: resetSelectedType,
            allowSenderMeetingLink: editorConfig[selectedType].allowSenderMeetingLink
          })
        })]
      });
    case 'DOCUMENTS':
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(InlineInsertHeader, {
          onBackClick: resetSelectedType,
          selectedType: selectedType
        }), /*#__PURE__*/_jsx(View, {
          paddingBlockStart: "300",
          paddingBlockEnd: "200",
          as: "div",
          children: /*#__PURE__*/_jsx(InsertDocuments, {
            outputType: editorConfig[selectedType].outputType,
            contactEmail: editorConfig[selectedType].contactEmail,
            onViewInsertMenu: handleViewInsertMenu,
            onInsertComplete: handleInsertComplete,
            onInsertContent: editorConfig[selectedType].onInsertContent,
            useContentContainer: true,
            resetSelectedType: resetSelectedType,
            onToggleAllowDownload: editorConfig[selectedType].onToggleAllowDownload
          })
        })]
      });
    default:
      Raven.captureMessage('SELECTED_TYPE_UNKNOWN');
      return null;
  }
}